import React, { type ReactNode } from 'react';
import Report from '@uber/base-icons/report';
import { useStyletron } from 'baseui';
import { ARTWORK_TYPE, Banner, KIND as BannerKIND } from 'baseui/banner';
import { Skeleton } from 'baseui/skeleton';
import type { StyleObject } from 'styletron-react';
import { useAPIProvider } from '../../../common/hooks/use-api-provider';
import Questionnaire from '../../../common/questionnaire-panel/questionnaire';
import { partitionStaticAndDynamicQuestionnaire } from '../../../common/side-popover-panel/utils';
import type { AnswerT } from '../../../common/types/types';
import { useQuestionnaire } from '../hooks/use-questionnaire';
import { SectionWrapper } from './section-wrapper';
type LabelQuestionnaireProps = {
  header?: ReactNode;
  questionnaireId: string;
  answers: AnswerT;
  onChange: (isAnswerValid: boolean, answers?: AnswerT) => void;
  style?: StyleObject;
  isQuestionnaireComposable?: boolean;
};
const SKELETON_HEIGHT = '30px';
const Loading = () => {
  const [css, theme] = useStyletron();
  const items = Array.from({
    length: 2
  }, (_, index) => index);
  return <section className={css({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.sizing.scale850
  })}>
      {items.map(item => <div key={item} className={css({
      display: 'flex',
      flexDirection: 'column',
      gap: theme.sizing.scale400
    })}>
          <Skeleton height={SKELETON_HEIGHT} width="100%" animation />
          <Skeleton height="100px" width="100%" animation />
        </div>)}
    </section>;
};
const Error = ({
  onRetry,
  error
}: {
  onRetry: () => void;
  error: string;
}) => {
  return <Banner kind={BannerKIND.negative} artwork={{
    icon: () => <Report size={30} />,
    type: ARTWORK_TYPE.badge
  }} title="Error" action={{
    label: 'Retry',
    onClick: onRetry
  }} overrides={{
    Root: {
      style: {
        margin: 0
      }
    },
    Message: {
      style: ({
        $theme
      }) => ({
        ...$theme.typography.LabelSmall,
        paddingTop: $theme.sizing.scale200,
        fontWeight: 'normal'
      })
    }
  }}>
      {error}
    </Banner>;
};
export const LabelQuestionnaire = ({
  header,
  questionnaireId,
  answers,
  onChange,
  style = {},
  isQuestionnaireComposable
}: LabelQuestionnaireProps) => {
  const {
    fetchQuestionnaire
  } = useAPIProvider();
  const {
    questionnaire,
    isLoading,
    error,
    retry
  } = useQuestionnaire({
    fetchQuestionnaire,
    answers,
    questionnaireId
  });
  const {
    staticQuestionnaire
  } = questionnaire ? partitionStaticAndDynamicQuestionnaire(questionnaire) : {};
  return <SectionWrapper title={header} style={style}>
      {isLoading && <Loading />}
      {staticQuestionnaire && <Questionnaire questionnaire={staticQuestionnaire} onChange={onChange} isQuestionnaireComposable={isQuestionnaireComposable} />}
      {error && <Error onRetry={retry} error={error} />}
    </SectionWrapper>;
};